/* eslint-disable react/prop-types */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Table,
  Tooltip,
  Space,
  Modal,
  Button,
  message,
  notification,
  Input,
  Form,
  DatePicker,
  ConfigProvider,
} from 'antd';
import { Button as CrceButton, Spinner, Checkbox as CrceCheckbox } from '@crc-evans/ui-components';
import {
  ArrowLeft,
  Upload,
  Plus,
  Calendar,
  FileText,
  ScrollText,
  Image as ImageIcon,
  Pencil,
  Trash2,
  Share2,
  Copy,
} from 'lucide-react';
import moment from 'moment';
import './ProjectHeatProfileList_styled.css';
import axiosConfig from '../../services/axiosConfig';
import crceTechnicalHeatProfileAxios, {
  isCrceTechnicalHeatProfileApiConfigured,
} from '../../redux/heatProfile/crceTechnicalHeatProfileAxiosConfig';

/** API error copy for notifications (avoids dumping HTML from 404 pages into the toast). */
function isHtmlErrorPayload(value) {
  if (typeof value !== 'string') return false;
  return (
    value.includes('<!DOCTYPE') ||
    value.includes('<html') ||
    /Cannot GET\s+\//i.test(value) ||
    value.includes('Error</title>')
  );
}

function friendlyHtmlErrorMessage(data) {
  if (!isHtmlErrorPayload(data)) return null;
  if (typeof data === 'string' && /Cannot GET\s+(\/[^\s<]+)/i.exec(data)) {
    const m = /Cannot GET\s+(\/[^\s<]+)/i.exec(data);
    const path = m ? m[1] : '';
    return `The server has no API route for ${path || 'this request'}. The request may have hit the wrong host: set REACT_APP_AWS_CRCE_TECHNICAL_HEAT_PROFILE or ensure REACT_APP_AWS_HEAT_PROFILE_DATABASE points at the service that implements GET /technical-heat-profile, then rebuild.`;
  }
  return 'The service returned an HTML error page instead of JSON. Check the CRCE technical API base URL (REACT_APP_AWS_CRCE_TECHNICAL_HEAT_PROFILE) and that this environment can reach that host.';
}

function getApiErrorDescription(error, fallback = 'Please try again later.') {
  if (error == null) return fallback;
  if (typeof error === 'string') {
    if (isHtmlErrorPayload(error)) return friendlyHtmlErrorMessage(error) || fallback;
    return error;
  }
  if (error.message && !error.response) {
    return error.message;
  }
  const data = error.response?.data;
  if (typeof data === 'string') {
    const msg = friendlyHtmlErrorMessage(data);
    if (msg) return msg;
    return data.length > 500 ? `${data.slice(0, 200)}…` : data;
  }
  const fromData =
    data?.message ||
    data?.error ||
    (Array.isArray(data?.errors) && data.errors.filter(Boolean).join(', ')) ||
    data?.name;
  return fromData || error.message || error.error || fallback;
}

function showApiErrorNotification(title, error, fallbackDescription) {
  notification.error({
    message: title,
    description: getApiErrorDescription(error, fallbackDescription),
  });
}

/** Validation / UX notices — same shape as `GenerateReport.js` & `ProjectHeatProfile.js`. */
function showValidationWarning(title, description) {
  notification.warning(
    description ? { message: title, description } : { message: title }
  );
}

function showValidationError(title, description) {
  notification.error(
    description ? { message: title, description } : { message: title }
  );
}

function showValidationSuccess(title, description) {
  notification.success(
    description ? { message: title, description } : { message: title }
  );
}

/** Import modal list: CRCE technical API (`REACT_APP_AWS_CRCE_TECHNICAL_HEAT_PROFILE`). */
const TECHNICAL_HEAT_PROFILE_LIST_PATH = 'technical-heat-profile';
/** Add selected profiles: PIH main API (`REACT_APP_BASE_URL` via services/axiosConfig). */
const PIH_PROJECT_HEAT_PROFILE_BULK_UPLOAD_PATH = '/project-heat-profile/bulk-upload';

const { Title } = Typography;

const EmailReportForm = ({ record, onSend, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      await onSend({
        emails: values.emails.split(',').map((email) => email.trim()),
        subject: values.subject,
        message: values.message,
      });
    } catch (error) {
      console.error('Error sending email:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={{
        subject: `Heat Profile Report - ${record?.reportNumber || 'N/A'}`,
        message: 'Please find attached the heat profile report for review and approval.',
      }}
    >
      <Form.Item
        label="Recipient Emails (comma separated)"
        name="emails"
        rules={[
          { required: true, message: 'Please enter at least one email address' },
          {
            validator: (_, value) => {
              if (!value) return Promise.resolve();
              const emails = value.split(',').map((email) => email.trim());
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              const invalidEmails = emails.filter((email) => !emailRegex.test(email));
              return invalidEmails.length > 0
                ? Promise.reject(new Error(`Invalid email(s): ${invalidEmails.join(', ')}`))
                : Promise.resolve();
            },
          },
        ]}
      >
        <Input.TextArea
          placeholder="engineer@pipeline-technique.com, manager@pipeline-technique.com"
          rows={2}
        />
      </Form.Item>

      <Form.Item
        label="Subject"
        name="subject"
        rules={[{ required: true, message: 'Please enter email subject' }]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label="Message"
        name="message"
        rules={[{ required: true, message: 'Please enter email message' }]}
      >
        <Input.TextArea rows={4} />
      </Form.Item>

      <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
        <Space>
          <CrceButton label="Cancel" variant="tertiary" size="medium" onClick={onCancel} />
          <CrceButton
            label="Send Email"
            variant="primary"
            size="medium"
            onClick={() => form.submit()}
            disabled={loading}
          />
        </Space>
      </Form.Item>
    </Form>
  );
};

const TECHNICAL_HEAT_PROFILE_IMPORT_PAGE = { pageNumber: 1, pageSize: 1000 };

const getImportHeatProfileRowKey = (row) =>
  row.id != null && row.id !== '' ? row.id : `${row.reportNumber}-${row.projectNumber}`;

function normalizeReportNumberForDedup(value) {
  if (value == null || value === '') return '';
  return String(value).trim().toLowerCase();
}

/** Project heat profile API may send report number under different keys or nested in `heatProfile`. */
function getRowReportNumber(row) {
  if (!row || typeof row !== 'object') return undefined;
  const directKeys = [
    'reportNumber',
    'ReportNumber',
    'report_no',
    'reportNo',
    'ReportNo',
    'heatProfileReportNumber',
    'reportId',
    'report_id',
  ];
  for (let i = 0; i < directKeys.length; i += 1) {
    const v = row[directKeys[i]];
    if (v != null && v !== '') {
      if (typeof v === 'string' || typeof v === 'number') return String(v).trim();
      if (typeof v === 'object' && v !== null && 'name' in v && v.name != null) {
        return String(v.name).trim();
      }
    }
  }
  const nested = [row.heatProfile, row.heat_profile, row.meta, row.data, row.attributes, row.properties];
  for (let i = 0; i < nested.length; i += 1) {
    const obj = nested[i];
    if (!obj || typeof obj !== 'object') continue;
    for (const k of ['reportNumber', 'ReportNumber', 'reportNo', 'name']) {
      const v = obj[k];
      if (v != null && v !== '' && (typeof v === 'string' || typeof v === 'number')) {
        return String(v).trim();
      }
    }
  }
  return undefined;
}

/** Matches ProjectHeatProfile: formData.reportAuthor || name || 'Project User' (no form here → logged-in user). */
function getProjectHeatProfileReportAuthor() {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return 'Project User';
    const user = JSON.parse(raw);
    if (user?.name) return user.name;
    const first = user['custom:first_name'] || '';
    const last = user['custom:last_name'] || '';
    const combined = `${first} ${last}`.trim();
    return combined || 'Project User';
  } catch {
    return 'Project User';
  }
}

/** API sends `createdAt` as calendar date string, e.g. "2024-09-18". */
const API_DATE_YMD = /^\d{4}-\d{2}-\d{2}$/;

/** Resolve created timestamp from common API shapes, nesting, and unknown *created* keys. */
function getRowCreatedAtForFilter(row) {
  if (!row || typeof row !== 'object') return undefined;
  const directKeys = [
    'createdAt',
    'CreatedAt',
    'created_at',
    'creationDate',
    'CreationDate',
    'creation_date',
    'dateCreated',
    'DateCreated',
    'created_time',
    'Created_Time',
    'insertDate',
    'InsertDate',
    'created',
    'Created',
  ];
  for (let i = 0; i < directKeys.length; i += 1) {
    const v = row[directKeys[i]];
    if (v != null && v !== '') return v;
  }
  const nested = [row.meta, row.data, row.attributes, row.properties, row.heatProfile];
  for (let i = 0; i < nested.length; i += 1) {
    const obj = nested[i];
    if (!obj || typeof obj !== 'object') continue;
    const nk = ['createdAt', 'CreatedAt', 'created_at', 'creationDate', 'CreationDate'];
    for (let j = 0; j < nk.length; j += 1) {
      const v = obj[nk[j]];
      if (v != null && v !== '') return v;
    }
  }
  const skipKey = /^(trailDate|trialDate|updatedAt|UpdatedAt|modifiedAt|ModifiedAt)$/i;
  const keys = Object.keys(row);
  for (let i = 0; i < keys.length; i += 1) {
    const key = keys[i];
    if (skipKey.test(key)) continue;
    if (!/created|creation|insert|timestamp/i.test(key)) continue;
    const v = row[key];
    if (v != null && v !== '' && typeof v !== 'object') return v;
  }
  return undefined;
}

function yyyyMmDdFromApiDate(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const ms = raw > 1e12 ? raw : raw * 1000;
    const m = moment(ms);
    return m.isValid() ? m.format('YYYY-MM-DD') : null;
  }
  const s = String(raw).trim();
  if (API_DATE_YMD.test(s)) return s;
  const head = s.slice(0, 10);
  if (API_DATE_YMD.test(head)) return head;
  let m = moment(s, 'YYYY-MM-DD', true);
  if (m.isValid()) return m.format('YYYY-MM-DD');
  m = moment(s);
  return m.isValid() ? m.format('YYYY-MM-DD') : null;
}

/** Filter state `createdDate` is YYYY-MM-DD; compare to row created field. */
function rowMatchesCreatedDateFilter(row, filterYyyyMmDd) {
  const filterNorm = filterYyyyMmDd != null ? String(filterYyyyMmDd).trim() : '';
  if (!filterNorm) return true;
  const ymd = yyyyMmDdFromApiDate(getRowCreatedAtForFilter(row));
  return ymd === filterNorm;
}

/** Table cells: "2025 May 07" (year, short month name, zero-padded day). */
const TABLE_DATE_DISPLAY_FORMAT = 'YYYY MMM DD';

function formatTableDateDisplay(raw) {
  if (raw == null || raw === '') return 'N/A';
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const ms = raw > 1e12 ? raw : raw * 1000;
    const m = moment(ms);
    return m.isValid() ? m.format(TABLE_DATE_DISPLAY_FORMAT) : 'N/A';
  }
  const s = String(raw).trim();
  const head = s.slice(0, 10);
  if (API_DATE_YMD.test(head)) {
    const md = moment(head, 'YYYY-MM-DD', true);
    return md.isValid() ? md.format(TABLE_DATE_DISPLAY_FORMAT) : 'N/A';
  }
  const m = moment(s);
  return m.isValid() ? m.format(TABLE_DATE_DISPLAY_FORMAT) : 'N/A';
}

/** CRCE list may return camelCase or PascalCase per row. */
function normalizeCrceImportRow(row) {
  if (!row || typeof row !== 'object') return row;
  return {
    ...row,
    id: row.id ?? row.Id,
    reportNumber: row.reportNumber ?? row.ReportNumber,
    projectName: row.projectName ?? row.ProjectName,
    projectNumber: row.projectNumber ?? row.ProjectNumber,
    createdAt: getRowCreatedAtForFilter(row),
  };
}

const ProjectHeatProfileList = (props) => {
  const { selectedProject, selectedSOW, onEdit, onCreateNew, setIsHeatProfileModalVisible } = props;

  // Internal back handler: closes the list modal if setVisible is provided
  const onBack = () => {
    setIsHeatProfileModalVisible(false);
  };

  // State variables
  const [projectHeatProfiles, setProjectHeatProfiles] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [searchFilters, setSearchFilters] = useState({
    reportNumber: '',
    projectName: '',
    projectNumber: '',
    reportAuthor: '',
    material: '',
    trialDate: '',
    createdDate: '',
  });

  // Modal states
  const [isReportModalVisible, setIsReportModalVisible] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isImportHeatProfileModalVisible, setIsImportHeatProfileModalVisible] = useState(false);
  const [selectedImportRowKeys, setSelectedImportRowKeys] = useState([]);
  const [importBulkSubmitLoading, setImportBulkSubmitLoading] = useState(false);
  const [projectHeatProfilesRefreshToken, setProjectHeatProfilesRefreshToken] = useState(0);
  const [crceTechnicalImportList, setCrceTechnicalImportList] = useState([]);
  const [crceTechnicalImportListLoading, setCrceTechnicalImportListLoading] = useState(false);
  const [importSearchFilters, setImportSearchFilters] = useState({
    reportNumber: '',
    projectName: '',
    projectNumber: '',
    createdDate: '',
  });
  const [importTablePagination, setImportTablePagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const importHeatProfileTableRows = useMemo(() => {
    const raw = crceTechnicalImportList;
    let rows = [];
    if (Array.isArray(raw)) rows = raw;
    else if (raw && typeof raw === 'object' && Array.isArray(raw.records)) rows = raw.records;
    else return [];

    const existingReportNumbers = new Set(
      projectHeatProfiles
        .map((p) => normalizeReportNumberForDedup(p.reportNumber))
        .filter((k) => k !== '')
    );

    return rows
      .filter((row) => {
        const r = normalizeCrceImportRow(row);
        const key = normalizeReportNumberForDedup(r.reportNumber);
        if (!key) return true;
        return !existingReportNumbers.has(key);
      })
      .map(normalizeCrceImportRow);
  }, [crceTechnicalImportList, projectHeatProfiles]);

  const handleOpenImportHeatProfileModal = async () => {
    if (!isCrceTechnicalHeatProfileApiConfigured) {
      showApiErrorNotification(
        'Import list unavailable in this build',
        new Error(
          'Set REACT_APP_AWS_CRCE_TECHNICAL_HEAT_PROFILE (CRCE technical API) or REACT_APP_AWS_HEAT_PROFILE_DATABASE (if that same service serves GET /technical-heat-profile), then rebuild. Both were missing in this bundle.'
        )
      );
      return;
    }
    setImportSearchFilters({
      reportNumber: '',
      projectName: '',
      projectNumber: '',
      createdDate: '',
    });
    setImportTablePagination({ current: 1, pageSize: 10 });
    setSelectedImportRowKeys([]);
    setIsImportHeatProfileModalVisible(true);
    setCrceTechnicalImportListLoading(true);
    try {
      const res = await crceTechnicalHeatProfileAxios.get(TECHNICAL_HEAT_PROFILE_LIST_PATH, {
        params: {
          pageNumber: TECHNICAL_HEAT_PROFILE_IMPORT_PAGE.pageNumber,
          pageSize: TECHNICAL_HEAT_PROFILE_IMPORT_PAGE.pageSize,
        },
      });
      const raw = res?.data?.data ?? res?.data;
      let rows = [];
      if (Array.isArray(raw)) {
        rows = raw;
      } else if (raw && typeof raw === 'object' && Array.isArray(raw.records)) {
        rows = raw.records;
      }
      setCrceTechnicalImportList(rows);
    } catch (error) {
      console.error('CRCE technical heat profile list fetch failed:', error);
      showApiErrorNotification(
        'Failed to load technical heat profiles for import',
        error,
        'Unable to load the import list. Please try again later.'
      );
      setCrceTechnicalImportList([]);
    } finally {
      setCrceTechnicalImportListLoading(false);
    }
  };

  const handleCloseImportHeatProfileModal = () => {
    setIsImportHeatProfileModalVisible(false);
    setSelectedImportRowKeys([]);
  };

  const handleAddHeatProfileFromImportModal = async () => {
    const selectedRows = importHeatProfileTableRows.filter((row) =>
      selectedImportRowKeys.some((k) => String(k) === String(getImportHeatProfileRowKey(row)))
    );

    if (selectedRows.length === 0) {
      showValidationWarning(
        'No heat profile selected',
        'Select at least one row in the table, then click Add Heat Profile.'
      );
      return;
    }
    if (!selectedProject?.id) {
      showValidationError(
        'Project not selected',
        'Choose a project before adding heat profiles to it.'
      );
      return;
    }

    setImportBulkSubmitLoading(true);
    try {
      const reportAuthor = getProjectHeatProfileReportAuthor();
      const projectId = selectedProject.id;
      const payload = {
        data: selectedRows.map((row) => ({
          ...row,
          projectId,
          reportAuthor,
        })),
      };

      const response = await axiosConfig.post(PIH_PROJECT_HEAT_PROFILE_BULK_UPLOAD_PATH, payload);

      if (response.status === 200 || response.status === 201) {
        showValidationSuccess(
          'Successfully added',
          response.data?.message ||
            `${selectedRows.length} heat profile(s) were added to the project.`
        );
        setProjectHeatProfilesRefreshToken((t) => t + 1);
        handleCloseImportHeatProfileModal();
      }
    } catch (error) {
      console.error('Bulk technical heat profile import failed:', error);
      showApiErrorNotification(
        'Failed to add heat profile(s)',
        error,
        'Unable to complete bulk upload. Please try again.'
      );
    } finally {
      setImportBulkSubmitLoading(false);
    }
  };


  // Fetch project heat profiles
  useEffect(() => {
    if (selectedProject?.id) {
      const fetchProjectHeatProfiles = async () => {
        setIsLoadingData(true);
        try {
          console.log('Fetching project heat profiles for project:', selectedProject.id);
          const response = await axiosConfig.get('/project-heat-profile', {
            params: {
              projectId: selectedProject.id,
            },
          });

          console.log('API Response:', response);

          if (response.status === 200) {
            const data = response.data;
            let processedData = [];

            if (Array.isArray(data)) {
              processedData = data;
            } else if (data && typeof data === 'object' && Array.isArray(data.data)) {
              processedData = data.data;
            } else if (data && typeof data === 'object' && Array.isArray(data.records)) {
              processedData = data.records;
            } else {
              console.warn('API returned non-array data:', data);
              processedData = [];
            }

            // Transform the data (normalize report # — API shape varies)
            const transformedData = processedData.map((item) => {
              const reportNumber = getRowReportNumber(item) ?? item.reportNumber ?? item.ReportNumber;
              return {
                ...item,
                reportNumber: reportNumber != null && String(reportNumber).trim() !== '' ? String(reportNumber).trim() : reportNumber,
                projectName: item.project?.projectName || item.projectName || 'N/A',
                projectNumber: item.project?.projectId || item.projectNumber || 'N/A',
              };
            });

            console.log('Setting transformed project heat profiles:', transformedData);
            setProjectHeatProfiles(transformedData);
          } else {
            console.warn('API returned non-200 status:', response.status);
            setProjectHeatProfiles([]);
          }
        } catch (error) {
          console.error('Error fetching project heat profiles:', error);
          showApiErrorNotification(
            'Failed to fetch project heat profiles',
            error,
            'Unable to load the list. Please try again later.'
          );
          setProjectHeatProfiles([]);
        } finally {
          setIsLoadingData(false);
        }
      };

      fetchProjectHeatProfiles();
    }
  }, [selectedProject, selectedSOW, projectHeatProfilesRefreshToken]);

  // Filter data based on search filters
  const filteredData = projectHeatProfiles.filter((item) => {
    return Object.keys(searchFilters).every((key) => {
      if (!searchFilters[key]) return true;

      if (key === 'createdDate') {
        return rowMatchesCreatedDateFilter(item, searchFilters.createdDate);
      }

      if (key === 'trialDate') {
        const raw = item.trailDate ?? item.trialDate;
        if (raw == null || raw === '') return false;
        const m = moment(raw);
        if (!m.isValid()) return false;
        return m.format('YYYY-MM-DD') === searchFilters.trialDate;
      }

      const value = key === 'reportNumber' ? getRowReportNumber(item) ?? item.reportNumber : item[key];
      if (typeof value === 'string') {
        return value.toLowerCase().includes(searchFilters[key].toLowerCase());
      }
      if (value !== undefined && value !== null) {
        return String(value).toLowerCase().includes(searchFilters[key].toLowerCase());
      }
      return false;
    });
  });

  // Handle search filter changes
  const normalizeCreatedDateFilterValue = (value) => {
    if (value == null || value === '') return '';
    const s = String(value).trim();
    if (API_DATE_YMD.test(s)) return s;
    const head = s.slice(0, 10);
    return API_DATE_YMD.test(head) ? head : s;
  };

  const handleSearchChange = (field, value) => {
    setSearchFilters((prev) => ({
      ...prev,
      [field]: field === 'createdDate' ? normalizeCreatedDateFilterValue(value) : value,
    }));
  };

  const handleImportSearchChange = (field, value) => {
    setImportSearchFilters((prev) => ({
      ...prev,
      [field]: field === 'createdDate' ? normalizeCreatedDateFilterValue(value) : value,
    }));
  };

  // Action handlers
  const handleView = (record) => {
    if (record.pdfReportDetails && record.pdfReportDetails.s3Url) {
      console.log('record.pdfReportDetails.s3Url:', record.pdfReportDetails.s3Url);
      window.open(record.pdfReportDetails.s3Url, '_blank');
    } else {
      message.warning('PDF report not available for this record.');
    }
  };

  const handleViewText = (record) => {
    if (record.textFileDetails && record.textFileDetails.s3Url) {
      window.open(record.textFileDetails.s3Url, '_blank');
    } else {
      message.warning('Text file not available for this record.');
    }
  };

  const handleViewImage = (record) => {
    if (record.imageFileDetails && record.imageFileDetails.s3Url) {
      window.open(record.imageFileDetails.s3Url, '_blank');
    } else {
      message.warning('Image file not available for this record.');
    }
  };

  const handleEdit = (record) => {
    console.log('Edit record:', record);
    setIsEditLoading(true);

    // Show updating message
    message.loading('Loading heat profile for editing...', 1.5).then(() => {
      if (onEdit) {
        onEdit(record);
      } else {
        message.info('Edit functionality will be implemented');
      }
      setIsEditLoading(false);
    });
  };

  const handleShare = (record) => {
    setSelectedRecord(record);
    setIsReportModalVisible(true);
  };

  const handleCopy = (record) => {
    // Copy record details to clipboard
    const recordInfo = `Heat Profile Details:
Report Number: ${record.reportNumber || 'N/A'}
Project Name: ${record.projectName || 'N/A'}
Project Number: ${record.projectNumber || 'N/A'}
Report Author: ${record.reportAuthor || 'N/A'}
Material: ${record.material || 'N/A'}
Trial Date: ${record.trailDate ? moment(record.trailDate).format('YYYY-MM-DD') : 'N/A'}
Created Date: ${yyyyMmDdFromApiDate(getRowCreatedAtForFilter(record)) ?? 'N/A'}`;

    navigator.clipboard
      .writeText(recordInfo)
      .then(() => {
        message.success('Heat profile details copied to clipboard');
      })
      .catch(() => {
        message.error('Failed to copy to clipboard');
      });
  };

  const handleSendReport = async (emailData) => {
    try {
      console.log('Sending report for record:', selectedRecord);
      const requestBody = {
        id: selectedRecord.id,
        recipientEmails: emailData.emails,
        subject: emailData.subject || `Heat Profile Report - ${selectedRecord.reportNumber}`,
        message:
          emailData.message ||
          'Please find attached the heat profile report for review and approval.',
        includeAttachments: true,
      };

      const response = await axiosConfig.post('/project-heat-profile/report', requestBody, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200) {
        message.success('Report sent successfully');
        setIsReportModalVisible(false);
        setSelectedRecord(null);
      }
    } catch (error) {
      console.error('Error sending report:', error);
      showApiErrorNotification(
        'Failed to send report',
        error,
        'The report could not be sent. Please try again.'
      );
    }
  };

  const showDeleteModal = (record) => {
    setSelectedRecord(record);
    setIsDeleteModalVisible(true);
  };

  const handleDelete = async () => {
    setIsDeleteLoading(true);
    try {
      await axiosConfig.delete(`/project-heat-profile/${selectedRecord.id}`);
      message.success('Project Heat Profile deleted successfully');

      // Refresh the list by removing the deleted item from local state
      setProjectHeatProfiles((prev) => prev.filter((item) => item.id !== selectedRecord.id));
    } catch (error) {
      console.error('Error deleting project heat profile:', error);
      showApiErrorNotification(
        'Failed to delete Project Heat Profile',
        error,
        'The record could not be deleted. Please try again.'
      );
    }
    setIsDeleteLoading(false);
    setIsDeleteModalVisible(false);
    setSelectedRecord(null);
  };

  const cancelDelete = () => {
    setIsDeleteModalVisible(false);
    setSelectedRecord(null);
  };

  // Create search inputs for table header (no search icon)
  const createSearchInput = (field, placeholder) => (
    <div className="searchEquip">
      <Input
        placeholder={placeholder}
        value={searchFilters[field]}
        onChange={(e) => handleSearchChange(field, e.target.value)}
        allowClear
      />
    </div>
  );

  /**
   * DatePicker for table header (mousedown stopPropagation) or toolbar (no wrapper).
   * Import modal uses toolbar — table header DatePickers often fail to commit in antd Table.
   */
  const createHeaderDatePicker = (field, placeholder, filters, setField, options = {}) => {
    const { wrapForTableHeader = true } = options;
    const raw = filters[field];
    const valueMoment =
      raw && moment(raw, 'YYYY-MM-DD', true).isValid() ? moment(raw, 'YYYY-MM-DD') : null;
    const picker = (
      <DatePicker
        className="pqt-hp-toolbar-picker"
        placeholder={placeholder}
        value={valueMoment}
        inputReadOnly
        onChange={(date) => setField(field, date ? date.format('YYYY-MM-DD') : '')}
        style={
          wrapForTableHeader
            ? { width: '100%', marginBottom: 8 }
            : { minWidth: 160, width: '100%', maxWidth: 240 }
        }
        allowClear
        suffixIcon={
          <span className="pqt-hp-picker-suffix-icon" aria-hidden>
            <Calendar size={16} strokeWidth={2} />
          </span>
        }
        getPopupContainer={() => document.body}
        dropdownClassName="pqt-hp-datepicker-dropdown modern-dropdown-popup"
        dropdownStyle={{ zIndex: 11000 }}
      />
    );
    if (wrapForTableHeader) {
      return (
        <div role="presentation" onMouseDown={(e) => e.stopPropagation()}>
          {picker}
        </div>
      );
    }
    return picker;
  };

  const createDateInput = (field, placeholder) =>
    createHeaderDatePicker(field, placeholder, searchFilters, handleSearchChange);

  const createImportSearchInput = (field, placeholder) => (
    <div className="searchEquip">
      <Input
        placeholder={placeholder}
        value={importSearchFilters[field]}
        onChange={(e) => handleImportSearchChange(field, e.target.value)}
        allowClear
      />
    </div>
  );

  const createImportDateInput = (field, placeholder) =>
    createHeaderDatePicker(field, placeholder, importSearchFilters, handleImportSearchChange);

  const filteredImportHeatProfileTableRows = useMemo(() => {
    return importHeatProfileTableRows.filter((item) => {
      return Object.keys(importSearchFilters).every((key) => {
        if (!importSearchFilters[key]) return true;

        if (key === 'createdDate') {
          return rowMatchesCreatedDateFilter(item, importSearchFilters.createdDate);
        }

        const value = item[key];
        if (typeof value === 'string') {
          return value.toLowerCase().includes(importSearchFilters[key].toLowerCase());
        }
        if (value !== undefined && value !== null) {
          return String(value).toLowerCase().includes(importSearchFilters[key].toLowerCase());
        }
        return false;
      });
    });
  }, [importHeatProfileTableRows, importSearchFilters]);

  useEffect(() => {
    setImportTablePagination((p) => ({ ...p, current: 1 }));
  }, [
    importSearchFilters.reportNumber,
    importSearchFilters.projectName,
    importSearchFilters.projectNumber,
    importSearchFilters.createdDate,
  ]);

  useEffect(() => {
    const total = filteredImportHeatProfileTableRows.length;
    const { current, pageSize } = importTablePagination;
    if (total === 0) return;
    const lastPage = Math.max(1, Math.ceil(total / pageSize));
    if (current > lastPage) {
      setImportTablePagination((p) => ({ ...p, current: lastPage }));
    }
  }, [filteredImportHeatProfileTableRows.length, importTablePagination.current, importTablePagination.pageSize]);

  const importCurrentPageRows = useMemo(() => {
    const { current, pageSize } = importTablePagination;
    const start = (current - 1) * pageSize;
    return filteredImportHeatProfileTableRows.slice(start, start + pageSize);
  }, [filteredImportHeatProfileTableRows, importTablePagination]);

  const importHeatProfileColumns = useMemo(() => {
    const pageKeys = importCurrentPageRows.map((r) => getImportHeatProfileRowKey(r));
    const selectedOnPage = pageKeys.filter((k) =>
      selectedImportRowKeys.some((x) => String(x) === String(k))
    );
    const allOnPage = pageKeys.length > 0 && selectedOnPage.length === pageKeys.length;
    const someOnPage = selectedOnPage.length > 0 && !allOnPage;

    return [
      {
        title: (
          <div className="pqt-hp-import-select-col pqt-hp-import-select-col--head">
            <CrceCheckbox
              checked={allOnPage}
              indeterminate={someOnPage}
              onChange={(v) => {
                const checked = typeof v === 'boolean' ? v : (v?.target?.checked ?? false);
                if (checked) {
                  setSelectedImportRowKeys((prev) => {
                    const have = new Set(prev.map((x) => String(x)));
                    const out = [...prev];
                    importCurrentPageRows.forEach((row) => {
                      const key = getImportHeatProfileRowKey(row);
                      if (!have.has(String(key))) {
                        have.add(String(key));
                        out.push(key);
                      }
                    });
                    return out;
                  });
                } else {
                  setSelectedImportRowKeys((prev) =>
                    prev.filter((k) => !pageKeys.some((pk) => String(pk) === String(k)))
                  );
                }
              }}
              borderColor="#00C9A7"
              aria-label="Select all on this page"
            />
          </div>
        ),
        key: 'import_select',
        width: 56,
        align: 'center',
        fixed: 'left',
        render: (_, record) => {
          const k = getImportHeatProfileRowKey(record);
          const checked = selectedImportRowKeys.some((x) => String(x) === String(k));
          return (
            <div className="pqt-hp-import-select-col">
              <CrceCheckbox
                checked={checked}
                onChange={(v) => {
                  const next = typeof v === 'boolean' ? v : (v?.target?.checked ?? false);
                  if (next) {
                    setSelectedImportRowKeys((prev) => {
                      if (prev.some((x) => String(x) === String(k))) return prev;
                      return [...prev, k];
                    });
                  } else {
                    setSelectedImportRowKeys((prev) => prev.filter((x) => String(x) !== String(k)));
                  }
                }}
                borderColor="#00C9A7"
                aria-label="Select row"
              />
            </div>
          );
        },
      },
      {
        title: (
          <div className="pqt-hp-col-head">
            <div className="pqt-hp-col-title">Report Number</div>
            {createImportSearchInput('reportNumber', 'Search')}
          </div>
        ),
        dataIndex: 'reportNumber',
        key: 'reportNumber',
        width: 200,
        ellipsis: true,
        render: (text) => <span className="pqt-hp-import-cell-text">{text || 'N/A'}</span>,
      },
      {
        title: (
          <div className="pqt-hp-col-head">
            <div className="pqt-hp-col-title">Project Name</div>
            {createImportSearchInput('projectName', 'Search')}
          </div>
        ),
        dataIndex: 'projectName',
        key: 'projectName',
        width: 240,
        ellipsis: true,
        render: (text) => <span className="pqt-hp-import-cell-text">{text || 'N/A'}</span>,
      },
      {
        title: (
          <div className="pqt-hp-col-head">
            <div className="pqt-hp-col-title">Project Number</div>
            {createImportSearchInput('projectNumber', 'Search')}
          </div>
        ),
        dataIndex: 'projectNumber',
        key: 'projectNumber',
        width: 180,
        ellipsis: true,
        render: (text) => <span className="pqt-hp-import-cell-text">{text || 'N/A'}</span>,
      },
      {
        title: (
          <div className="pqt-hp-col-head">
            <div className="pqt-hp-col-title">Created Date</div>
            {createImportDateInput('createdDate', 'Select date')}
          </div>
        ),
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 160,
        align: 'center',
        render: (_, record) => (
          <span className="pqt-hp-import-cell-text">
            {formatTableDateDisplay(getRowCreatedAtForFilter(record))}
          </span>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- createImport* helpers use latest importSearchFilters each render
  }, [importCurrentPageRows, selectedImportRowKeys, importSearchFilters]);

  // Table columns with search filters
  const columns = [
    {
      title: (
        <div className="pqt-hp-col-head">
          <div className="pqt-hp-col-title">Report Number</div>
          {createSearchInput('reportNumber', 'Search')}
        </div>
      ),
      dataIndex: 'reportNumber',
      key: 'reportNumber',
      width: 180,
      render: (text, record) => {
        const display = getRowReportNumber(record) ?? text;
        return (
          <span className="pqt-hp-cell-report-number">{display != null && String(display).trim() !== '' ? String(display) : 'N/A'}</span>
        );
      },
    },
    {
      title: (
        <div className="pqt-hp-col-head">
          <div className="pqt-hp-col-title">Project Name</div>
          {createSearchInput('projectName', 'Search')}
        </div>
      ),
      dataIndex: 'projectName',
      key: 'projectName',
      width: 200,
      render: (text) => text || 'N/A',
    },
    {
      title: (
        <div className="pqt-hp-col-head">
          <div className="pqt-hp-col-title">Project Number</div>
          {createSearchInput('projectNumber', 'Search')}
        </div>
      ),
      dataIndex: 'projectNumber',
      key: 'projectNumber',
      width: 150,
      render: (text) => text || 'N/A',
    },
    {
      title: (
        <div className="pqt-hp-col-head">
          <div className="pqt-hp-col-title">Report Author</div>
          {createSearchInput('reportAuthor', 'Search')}
        </div>
      ),
      dataIndex: 'reportAuthor',
      key: 'reportAuthor',
      width: 150,
      render: (text) => text || 'N/A',
    },
    {
      title: (
        <div className="pqt-hp-col-head">
          <div className="pqt-hp-col-title">Material</div>
          {createSearchInput('material', 'Search')}
        </div>
      ),
      dataIndex: 'material',
      key: 'material',
      width: 180,
      render: (text) => text || '—',
    },
    {
      title: (
        <div className="pqt-hp-col-head">
          <div className="pqt-hp-col-title">Trial Date</div>
          {createDateInput('trialDate', 'Select date')}
        </div>
      ),
      dataIndex: 'trailDate',
      key: 'trailDate',
      width: 120,
      render: (text) => formatTableDateDisplay(text),
    },
    {
      title: (
        <div className="pqt-hp-col-head">
          <div className="pqt-hp-col-title">Created Date</div>
        </div>
      ),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (_, record) => formatTableDateDisplay(getRowCreatedAtForFilter(record)),
    },
    {
      title: <div className="pqt-hp-col-title">Files</div>,
      key: 'files',
      width: 128,
      align: 'center',
      render: (_, record) => (
        <div className="pqt-hp-file-actions">
          <Tooltip title={record.pdfReportDetails?.s3Url ? 'View PDF' : 'PDF not available'}>
            <button
              type="button"
              className="pqt-hp-icon-btn"
              disabled={!record.pdfReportDetails?.s3Url}
              aria-label="View PDF"
              onClick={() => record.pdfReportDetails?.s3Url && handleView(record)}
            >
              <FileText size={16} strokeWidth={1.75} />
            </button>
          </Tooltip>
          <Tooltip title={record.textFileDetails?.s3Url ? 'View document' : 'Document not available'}>
            <button
              type="button"
              className="pqt-hp-icon-btn"
              disabled={!record.textFileDetails?.s3Url}
              aria-label="View text file"
              onClick={() => record.textFileDetails?.s3Url && handleViewText(record)}
            >
              <ScrollText size={16} strokeWidth={1.75} />
            </button>
          </Tooltip>
          <Tooltip title={record.imageFileDetails?.s3Url ? 'View images' : 'Images not available'}>
            <button
              type="button"
              className="pqt-hp-icon-btn"
              disabled={!record.imageFileDetails?.s3Url}
              aria-label="View images"
              onClick={() => record.imageFileDetails?.s3Url && handleViewImage(record)}
            >
              <ImageIcon size={16} strokeWidth={1.75} />
            </button>
          </Tooltip>
        </div>
      ),
    },
    {
      title: <div className="pqt-hp-col-title">Actions</div>,
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <div className="pqt-hp-row-actions">
          <Tooltip title="Edit">
            <button
              type="button"
              className="pqt-hp-icon-btn"
              aria-label="Edit"
              disabled={isEditLoading}
              onClick={() => handleEdit(record)}
            >
              <Pencil size={16} strokeWidth={1.75} />
            </button>
          </Tooltip>
          <Tooltip title="Delete">
            <button
              type="button"
              className="pqt-hp-icon-btn pqt-hp-icon-btn--danger"
              aria-label="Delete"
              onClick={() => showDeleteModal(record)}
            >
              <Trash2 size={16} strokeWidth={1.75} />
            </button>
          </Tooltip>
          <Tooltip title="Share">
            <button
              type="button"
              className="pqt-hp-icon-btn"
              aria-label="Share"
              onClick={() => handleShare(record)}
            >
              <Share2 size={16} strokeWidth={1.75} />
            </button>
          </Tooltip>
          <Tooltip title="Copy">
            <button type="button" className="pqt-hp-icon-btn" aria-label="Copy" onClick={() => handleCopy(record)}>
              <Copy size={16} strokeWidth={1.75} />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="pqt-heat-profile-manage">
      <header className="pqt-heat-profile-manage__header">
        <div className="pqt-heat-profile-manage__header-left">
          <CrceButton
            label="Back"
            variant="tertiary"
            size="medium"
            icon={ArrowLeft}
            onClick={onBack}
            aria-label="Close heat profile list"
          />
          <Title level={4} className="pqt-heat-profile-manage__title">
            List of Heat profile
          </Title>
        </div>
        <CrceButton
          label="Import Heat Profile"
          variant="secondary"
          size="medium"
          icon={Upload}
          onClick={handleOpenImportHeatProfileModal}
        />
      </header>

      <div className="pqt-heat-profile-manage__body">
        <div className="pqt-heat-profile-manage__filter-row">
          <span className="pqt-heat-profile-manage__filter-label">Filter by created date</span>
          {createHeaderDatePicker(
            'createdDate',
            'Select date',
            searchFilters,
            handleSearchChange,
            { wrapForTableHeader: false }
          )}
        </div>
        <div className="heat-profile-table-wrap">
          <Table
            className="heat-profile-table"
            dataSource={filteredData}
            columns={columns}
            loading={{
              spinning: isLoadingData,
              indicator: <Spinner variant="inline" size="large" color="#00C9A7" />,
            }}
            rowKey="id"
            size="small"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showLessItems: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              className: 'heat-profile-pagination pqt-hp-pagination--modern',
            }}
            scroll={{ x: 1200 }}
          />
        </div>
      </div>

      {/* Email Report Modal (share) */}
      <Modal
        title={<span className="pqt-hp-submodal-heading">Send heat profile report</span>}
        open={isReportModalVisible}
        onCancel={() => {
          setIsReportModalVisible(false);
          setSelectedRecord(null);
        }}
        footer={null}
        width={Math.min(560, typeof window !== 'undefined' ? window.innerWidth - 32 : 560)}
        centered
        destroyOnClose
        wrapClassName="pqt-heat-profile-submodal pqt-heat-profile-submodal--email"
        className="pqt-hp-submodal-dialog"
        bodyStyle={{ padding: '20px 24px 24px' }}
        maskStyle={{ background: 'rgba(15, 23, 42, 0.45)', backdropFilter: 'none' }}
        maskClosable
      >
        <div className="pqt-hp-submodal-intro">
          Share the heat profile report by email. Report number:{' '}
          <strong>{selectedRecord?.reportNumber != null ? String(selectedRecord.reportNumber) : '—'}</strong>
        </div>
        <EmailReportForm
          record={selectedRecord}
          onSend={handleSendReport}
          onCancel={() => {
            setIsReportModalVisible(false);
            setSelectedRecord(null);
          }}
        />
      </Modal>

      {/* Import: technical list from REACT_APP_AWS_CRCE_TECHNICAL_HEAT_PROFILE */}
      <Modal
        title={
          <div className="pqt-hp-import-modal-title-row">
            <div className="pqt-hp-import-modal-title-left">
              <CrceButton
                label="Back"
                variant="tertiary"
                size="medium"
                icon={ArrowLeft}
                onClick={handleCloseImportHeatProfileModal}
                aria-label="Close import heat profile"
              />
              <Title level={4} className="pqt-hp-import-modal-heading">
                Import Heat Profile
              </Title>
            </div>
            <CrceButton
              label="Add Heat Profile"
              variant="primary"
              size="medium"
              icon={Plus}
              onClick={handleAddHeatProfileFromImportModal}
              disabled={importBulkSubmitLoading}
            />
          </div>
        }
        open={isImportHeatProfileModalVisible}
        onCancel={handleCloseImportHeatProfileModal}
        footer={null}
        width="min(1320px, 98vw)"
        wrapClassName="pqt-heat-profile-import-modal"
        destroyOnClose
        className="pqt-hp-import-modal-dialog"
        bodyStyle={{ background: '#f1f5f9', padding: '0 0 8px' }}
      >
        <ConfigProvider
          getPopupContainer={(triggerNode) =>
            triggerNode?.closest?.('.ant-modal') || document.body
          }
        >
          <div className="pqt-hp-import-modal-content">
            <div className="pqt-hp-import-toolbar pqt-hp-import-toolbar--card">
              <span className="pqt-heat-profile-manage__filter-label">Filter by created date</span>
              {createHeaderDatePicker(
                'createdDate',
                'Select date',
                importSearchFilters,
                handleImportSearchChange,
                { wrapForTableHeader: false }
              )}
            </div>
            <div className="pqt-hp-import-table-wrap heat-profile-table-wrap--modern">
              <Table
                rowKey={(row) => getImportHeatProfileRowKey(row)}
                columns={importHeatProfileColumns}
                dataSource={filteredImportHeatProfileTableRows}
                loading={{
                  spinning: crceTechnicalImportListLoading,
                  indicator: <Spinner variant="inline" size="large" color="#00C9A7" />,
                }}
                pagination={{
                  current: importTablePagination.current,
                  pageSize: importTablePagination.pageSize,
                  total: filteredImportHeatProfileTableRows.length,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showLessItems: true,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                  className: 'heat-profile-pagination pqt-hp-import-pagination pqt-hp-pagination--modern',
                  onChange: (page, pageSize) => {
                    setImportTablePagination((prev) => ({
                      current: page,
                      pageSize: pageSize !== undefined ? pageSize : prev.pageSize,
                    }));
                  },
                  onShowSizeChange: (_page, size) => {
                    setImportTablePagination({ current: 1, pageSize: size });
                  },
                }}
                size="middle"
                scroll={{ x: 900 }}
                className="pqt-hp-import-table heat-profile-table"
              />
            </div>
          </div>
        </ConfigProvider>
      </Modal>

      {/* Delete confirmation */}
      <Modal
        title={<span className="pqt-hp-submodal-heading pqt-hp-submodal-heading--danger">Delete project heat profile</span>}
        open={isDeleteModalVisible}
        onCancel={cancelDelete}
        footer={
          <div className="pqt-hp-submodal-footer">
            <CrceButton
              label="Cancel"
              variant="tertiary"
              size="medium"
              onClick={cancelDelete}
              disabled={isDeleteLoading}
            />
            <Button
              type="primary"
              danger
              className="pqt-hp-submodal-btn-danger"
              loading={isDeleteLoading}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </div>
        }
        centered
        destroyOnClose
        width={Math.min(440, typeof window !== 'undefined' ? window.innerWidth - 32 : 440)}
        wrapClassName="pqt-heat-profile-submodal pqt-heat-profile-submodal--delete"
        className="pqt-hp-submodal-dialog"
        bodyStyle={{ padding: '20px 24px 8px' }}
        maskStyle={{ background: 'rgba(15, 23, 42, 0.45)', backdropFilter: 'none' }}
        maskClosable={!isDeleteLoading}
      >
        <div className="pqt-hp-submodal-delete-body">
          <p className="pqt-hp-submodal-delete-lead">
            This removes the heat profile from the project list. You can re-import it from the technical library if
            needed.
          </p>
          {selectedRecord ? (
            <p className="pqt-hp-submodal-delete-target">
              Report number:{' '}
              <span>
                {typeof selectedRecord.reportNumber === 'string'
                  ? selectedRecord.reportNumber
                  : selectedRecord.reportNumber != null
                    ? String(selectedRecord.reportNumber)
                    : 'N/A'}
              </span>
            </p>
          ) : null}
          <p className="pqt-hp-submodal-delete-warning">This action cannot be undone.</p>
        </div>
      </Modal>
    </div>
  );
};

export default ProjectHeatProfileList;
